import { type JSX } from "react";

import { useVoss } from "../state/VossProvider";
import { Badge, IconButton, Kbd } from "./primitives";
import { IconMenu, IconMoon, IconSettings, IconSparkle, IconSun } from "./icons";

/** Top bar: workspace context, command palette, fixture/memory indicators, theme + settings. */
export function TopBar({ onOpenSettings, onOpenDebug, onOpenPalette, onToggleSidebar }: { onOpenSettings: () => void; onOpenDebug: () => void; onOpenPalette: () => void; onToggleSidebar: () => void }): JSX.Element {
  const { currentWorkspace, memory, fixtureMode, theme, toggleTheme, debug } = useVoss();
  const enabledMemory = memory.filter((m) => m.enabled).length;

  return (
    <header className="voss-topbar">
      <IconButton label="Open menu" className="voss-menu-btn" onClick={onToggleSidebar}>
        <IconMenu />
      </IconButton>
      <div className="voss-topbar__id">
        <div className="voss-topbar__title">{currentWorkspace?.label ?? "VOSS"}</div>
        {/* The "Conversation" subtitle is noise on a phone; the local-only status is meaningful, so it stays. */}
        <div className={`voss-topbar__sub${currentWorkspace?.localOnly ? " voss-topbar__sub--lock" : " voss-topbar__sub--generic"}`}>
          {currentWorkspace?.localOnly ? "Local-only workspace" : "Conversation"}
        </div>
      </div>
      <div className="voss-spacer" />
      {/* ⌘K/search is meaningless on touch — hidden on mobile (CSS); the menu + composer are the mobile entry points. */}
      <button type="button" className="voss-cmdk" onClick={onOpenPalette} aria-label="Open command palette">
        <span>Search or run…</span>
        <Kbd>⌘K</Kbd>
      </button>
      {fixtureMode ? (
        <Badge tone="warn" dot>
          Fixture mode
        </Badge>
      ) : null}
      <Badge tone="accent">
        <IconSparkle width={12} height={12} /> <span className="voss-topbar__mem-count">{enabledMemory}</span>
        <span className="voss-topbar__mem-label">&nbsp;{enabledMemory === 1 ? "memory" : "memories"}</span>
      </Badge>
      {debug ? (
        <button type="button" className="voss-btn voss-btn--ghost" style={{ borderColor: "var(--border)" }} onClick={onOpenDebug}>
          Debug
        </button>
      ) : null}
      <IconButton label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"} onClick={toggleTheme}>
        {theme === "dark" ? <IconSun /> : <IconMoon />}
      </IconButton>
      <IconButton label="Settings" onClick={onOpenSettings}>
        <IconSettings />
      </IconButton>
    </header>
  );
}
